import type { ReactElement } from 'react';
import { Fragment } from 'react';

/**
 * Splits ts_headline output on its exact `<mark>…</mark>` delimiters ONLY.
 * Everything else (including any stray `<`/`>` characters) is rendered as
 * plain text — never parsed as HTML.
 */
const MARK_SPLIT_RE = /(<mark>[\s\S]*?<\/mark>)/g;
const MARK_UNWRAP_RE = /^<mark>([\s\S]*)<\/mark>$/;

/**
 * Renders a `search.query` result row's `snippetHtml` (F8) SAFELY. The task
 * spec calls for `isomorphic-dompurify` sanitization of `ts_headline` output
 * before any `dangerouslySetInnerHTML` — but `isomorphic-dompurify` isn't
 * installed in this workspace's `node_modules` and adding it would require a
 * `pnpm install`, which this task's constraints forbid. Sanitizing an HTML
 * *parser* is a defense-in-depth measure for content that still gets
 * interpreted as markup; this component sidesteps the need for one entirely
 * by never interpreting the string as HTML at all — it splits on the exact
 * `<mark>…</mark>` token pairs the `search_index` worker's `ts_headline`
 * call is contracted to produce and renders every other character as inert
 * React text (auto-escaped, never executed), so even a hostile/malformed
 * snippet can only ever display as visible text, never inject markup or
 * script. This is a stricter guarantee than an HTML-sanitizer allowlist and
 * needs no new dependency.
 *
 * OPEN ISSUE: once `isomorphic-dompurify` is actually installed in this
 * workspace (`apps/web/package.json` + a real `pnpm install`), this can be
 * swapped for the CODE_GUIDELINES-prescribed `dangerouslySetInnerHTML` +
 * DOMPurify allowlist pattern if a richer highlight/markup contract is ever
 * needed — functionally this component is a safe superset for the current
 * `<mark>`-only contract.
 */
export function HighlightedSnippet({ html }: { html: string }): ReactElement {
  const parts = html.split(MARK_SPLIT_RE).filter((part) => part.length > 0);

  return (
    <>
      {parts.map((part, index) => {
        const match = MARK_UNWRAP_RE.exec(part);
        const inner = match?.[1];
        if (match && inner !== undefined) {
          return <mark key={index}>{inner}</mark>;
        }
        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
}
